import React from 'react';
import PropTypes from 'prop-types';
import bindAll from 'lodash.bindall';
import {injectIntl, intlShape} from 'react-intl';
import VM from 'scratch-vm';
import {ICONS} from '../../lib/curo-icons.js';
import {translate, refine, setAsk, answerQuestion, extensionHelp, aiExtGuard, describe, applyToVm, undoApply, isIntact} from '../../lib/vibe/vibe-core.js';
import '../../lib/vibe/vibe-en.js'; // 영어 번역기를 'en' 으로 등록(부작용 import)
import '../../lib/vibe/vibe-aiext.js'; // AI·데이터 확장(날씨·차트·챗GPT·카메라 인식…) 규칙 등록(부작용 import). 빼면 지킴이가 도움 상자로 안내
// AI 도움 층(선택, 선생님 키) — 규칙이 못 알아들었을 때만 부른다. 설계: vibe-ai.js 머리말
// 상황 맞춤 예시(실제로 되는 것만)·블록 설명(2026-09-28)
import {suggestChips, explainIr, isExplainRequest} from '../../lib/vibe/vibe-assist.js';
import {aiHelp, isOn as aiIsOn, setOn as aiSetOn, getKey as aiGetKey, setKey as aiSetKey, ready as aiReady} from '../../lib/vibe/vibe-ai.js';

/**
 * 바이브 코딩(말로 코딩) 입력창 — 코드 탭 블록 작업 영역 위에 뜬다.
 * 설계: docs/ANALYSIS_vibe_coding_2026-09-23.md
 *
 * ⭐ 교육 장치가 화면의 핵심이다. 블록만 만들어 주고 끝내지 않는다:
 *   ① 학생 문장에 블록 카테고리 색을 칠해 "말 → 블록" 대응을 보여 주고
 *   ② 번역기가 대신 정한 것(기본값·무한 반복 등)을 알림으로 알려 주고, 고를 수 있게(되묻기) 하고
 *   ③ 고쳐 말하면(바이브 루프) 무엇이 몇에서 몇으로 바뀌었는지 보여 주고
 *   ④ 못 알아들은 말은 주석으로 남겨 "직접 만들어 볼래요?"로 넘긴다.
 *
 * 노출: 2026-09-24 부터 기본으로 보인다(대표 결정). ?vibe=0 이면 꺼지고 ?vibe=1 이면 다시 켜진다(브라우저에 기억).
 *       번역기는 한국어·영어를 알아듣는다(2026-09-30~) → 에디터 언어가 한국어면 한국어, 영어면 영어로 보이고, 그 밖의 언어에서는 숨긴다.
 *       (다른 언어 사용자가 한국어/영어로 말할 수는 있지만, 화면 문구·알림을 그 언어로 못 보여 주므로 숨기는 쪽을 골랐다.
 *        되돌리는 법: render() 의 uiLang() 이 null 을 돌려주는 조건을 바꾸면 된다.)
 * 개인정보: 학생 문장은 이 브라우저 안에서만 처리하고 어디로도 보내지 않는다.
 *          단, 선생님이 "AI 도움"을 켜고 OpenAI 키를 넣으면, 규칙이 못 알아들은 문장(과 지금 블록 요약)만 OpenAI 로 보낸다.
 *          켜기 전에 이 사실을 화면에 알리고 동의(켜기 버튼)를 받는다. 끄면 다시 이 브라우저 안에서만.
 */

// 2026-09-24 대표 "숨기지 말고 오픈" → 기본으로 보인다(한국어·영어 화면에서만 — render 의 uiLang).
// 끄고 싶은 브라우저는 ?vibe=0(기억), 다시 켜려면 ?vibe=1. 되돌리기(다시 숨김): 마지막 줄을 `=== '1'` 로.
const FLAG = 'curoai_vibe';
export const isVibeEnabled = () => {
    try {
        const q = window.location.search || '';
        if (/[?&]vibe=1(?:&|$)/.test(q)) localStorage.setItem(FLAG, '1');
        if (/[?&]vibe=0(?:&|$)/.test(q)) localStorage.setItem(FLAG, '0');
        return localStorage.getItem(FLAG) !== '0';
    } catch (e) {
        return true;
    }
};

// 스크래치 블록 카테고리 색 — 문장에 칠하는 색과 블록 색이 같아야 대응이 보인다
const CAT = {events: '#FFBF00', motion: '#4C97FF', looks: '#9966FF', sound: '#CF63CF', control: '#FFAB19', sensing: '#5CB1D6',
    operators: '#59C059', variables: '#FF8C1A', robot: '#0FBD8C'};
// 화면 문구 — 언어별(번역기 자체의 알림 문구는 vibe-core.js / vibe-en.js 가 만든다)
const UI = {
    ko: {
        cats: {events: '이벤트', motion: '동작', looks: '형태', sound: '소리', control: '제어', sensing: '감지', operators: '연산', variables: '변수', robot: '로봇'},
        examples: ['스페이스 키를 누르면 점프해', '계속 마우스를 따라가', '마우스에 닿으면 야옹 소리를 내고 점수를 1 올려', '모터 2개로 자동차 만들기', '장애물이 있으면 멈추고 없으면 앞으로 가'],
        chips: ['더 빨리', '계속 반복해', '그리고 소리도 내줘', '방금 거 취소해'],
        err: {
            stage: '지금은 무대가 선택되어 있어요. 아래 스프라이트 목록에서 스프라이트를 골라 주세요.',
            changed: '직접 고친 블록은 그대로 둘게요. 바꾸고 싶은 것을 처음부터 말해 주세요. 예: 스페이스 키를 누르면 앞으로 가',
            gone: '그 스프라이트가 없어져서 되돌릴 수 없어요.',
            empty: '알아들은 말이 없어요. 아래 예시처럼 말해 볼래요?'
        },
        title: '바이브 코딩', open: '바이브 코딩 열기', close: '닫기',
        sub: '말로 설명하면 블록이 만들어져요.',
        phRefine: '이어서 고쳐 말해 보세요. 예: 더 빨리',
        phNew: '예: 스페이스 키를 누르면 10만큼 움직이고 야옹 소리를 내',
        send: '보내기', make: '블록 만들기',
        // 2026-10-16 직원 의견 "되돌리기 단어가 애매모호함" → 버튼이 지금 할 일을 그대로 말한다
        undoBlocks: '만든 블록 지우기', undoChange: '고치기 전으로',
        undoTipBlocks: '방금 말로 만든 블록을 지워요. 직접 고친 블록은 지우지 않아요.',
        undoTipChange: '방금 고친 것을 취소하고 고치기 전 블록으로 돌아가요.',
        clear: '대화 지우기', clearTip: '대화 기록만 지워요. 만든 블록은 그대로 두고, 다음 말은 새로 만들어요.',
        dragTip: '끌어서 옮길 수 있어요. 두 번 누르면 원래 자리로 가요.',
        tryNew: '이렇게 말해 보세요', tryMore: '이어서 말해 보세요',
        detailShow: '자세히 보기', detailHide: '자세히 접기', detailNotes: n => ` · 알림 ${n}개`,
        changed: '바꿨어요', more: n => ` 외 ${n}개`, undoStep: '고치기 전으로 돌아갔어요.', removed: '만든 블록을 지웠어요',
        nothing: '알아들은 말이 없어요', made: (n, u) => `블록 ${n}개를 만들었어요${u ? ` · 모르는 말 ${u}개` : ''}`, onlyNotes: '모르는 말만 있어서 블록을 만들지 않았어요',
        madeKeep: n => `직접 고친 블록은 그대로 두고, 블록 ${n}개를 새로 만들었어요`,
        unknownTip: '아직 모르는 말', pickTitle: '골라 주세요', now: v => `지금: ${v}`,
        secChat: '대화', secChanges: '바꾼 것', secEcho: '이렇게 알아들었어요', dashed: '점선 = 아직 모르는 말', secBlocks: '지금 블록', secNotes: '알림',
        mic: {
            start: '말로 입력', stop: '듣는 중… 누르면 멈춰요', listening: '듣고 있어요. 말해 보세요…',
            ask: '크롬은 말소리를 구글 서버로 보내 글자로 바꿔요. 학생 이름 같은 개인정보는 말하지 않게 해 주세요.',
            ok: '마이크 켜기', cancel: '취소', denied: '마이크를 쓸 수 없어요. 주소창 왼쪽 아이콘 → 마이크 ‘허용’을 눌러 주세요.',
            none: '아무 말도 못 들었어요. 다시 눌러서 말해 보세요.', net: '음성 인식에 연결하지 못했어요. 인터넷을 확인해 주세요.'
        },
        explainFirst: '먼저 블록을 만들어 보세요. 그러면 어떻게 동작하는지 한 줄씩 알려 줄게요.',
        ai: {
            on: 'AI 도움 켜짐', off: 'AI 도움 (선생님용)', turnOn: '켜기', turnOff: '끄기', thinking: 'AI가 생각하는 중…',
            made: 'AI 도움으로 만들었어요', changed: 'AI 도움으로 고쳤어요', failed: 'AI 도움을 못 받아서 번역기 결과만 보여 줘요',
            keyPh: 'OpenAI API 키 (sk-…)', keyBad: 'sk- 로 시작하는 OpenAI 키를 넣어 주세요.',
            privacy: '번역기가 못 알아들은 문장만(지금 블록 요약과 함께) OpenAI로 보내 도움을 받아요. 키는 이 탭에만 저장되고, 비용은 이 키의 계정에서 나가요(문장당 약 1원). 학생 이름 같은 개인정보는 치지 않게 안내해 주세요.'
        }
    },
    en: {
        cats: {events: 'Events', motion: 'Motion', looks: 'Looks', sound: 'Sound', control: 'Control', sensing: 'Sensing', operators: 'Operators', variables: 'Variables', robot: 'Robot'},
        examples: ['when space is pressed jump', 'follow the mouse forever', 'if it touches the mouse play meow and add 1 to score', 'make a robot car with 2 motors', 'if there is an obstacle stop, otherwise go forward'],
        chips: ['faster', 'repeat forever', 'also play a sound', 'undo'],
        err: {
            stage: 'The stage is selected right now. Pick a sprite in the sprite list below.',
            changed: 'I\'ll leave the blocks you edited as they are. Tell me what you want from the start. e.g. when space is pressed move forward',
            gone: 'That sprite is gone, so I can\'t undo.',
            empty: 'I didn\'t understand anything. Try saying it like the examples below.'
        },
        title: 'Vibe Coding', open: 'Open vibe coding', close: 'Close',
        sub: 'Describe it in words and blocks appear.',
        phRefine: 'Keep talking to change it. e.g. faster',
        phNew: 'e.g. when space is pressed move 10 steps and play meow',
        send: 'Send', make: 'Make blocks',
        undoBlocks: 'Remove blocks', undoChange: 'Undo change',
        undoTipBlocks: 'Removes the blocks just made from your words. Blocks you edited yourself are never removed.',
        undoTipChange: 'Cancels the last change and goes back to the blocks before it.',
        clear: 'Clear chat', clearTip: 'Clears the chat only. Your blocks stay, and what you say next makes new blocks.',
        dragTip: 'Drag to move. Double-click to put it back.',
        tryNew: 'Try saying', tryMore: 'Keep going',
        detailShow: 'Show details', detailHide: 'Hide details', detailNotes: n => ` · ${n} note${n === 1 ? '' : 's'}`,
        changed: 'Changed', more: n => ` and ${n} more`, undoStep: 'Went back to before the change.', removed: 'Removed the blocks I made',
        nothing: 'I didn\'t understand anything', made: (n, u) => `Made ${n} block${n === 1 ? '' : 's'}${u ? ` · ${u} unknown part${u === 1 ? '' : 's'}` : ''}`, onlyNotes: 'Only words I don\'t know yet, so no blocks were made',
        madeKeep: n => `Left your edited blocks as they are and made ${n} new block${n === 1 ? '' : 's'}`,
        unknownTip: 'Not understood yet', pickTitle: 'Pick one', now: v => `now: ${v}`,
        secChat: 'Chat', secChanges: 'What changed', secEcho: 'What I understood', dashed: 'dashed = not understood yet', secBlocks: 'Blocks now', secNotes: 'Notes',
        mic: {
            start: 'Speak', stop: 'Listening… click to stop', listening: 'Listening. Go ahead and talk…',
            ask: 'Chrome sends your voice to Google to turn it into text. Please don\'t say personal information such as names.',
            ok: 'Turn on mic', cancel: 'Cancel', denied: 'The microphone is blocked. Click the icon left of the address bar and allow the microphone.',
            none: 'I didn’t hear anything. Click again and speak.', net: 'Couldn’t reach speech recognition. Check the internet connection.'
        },
        explainFirst: 'Make some blocks first, then I can explain them line by line.',
        ai: {
            on: 'AI help on', off: 'AI help (for teachers)', turnOn: 'Turn on', turnOff: 'Turn off', thinking: 'AI is thinking…',
            made: 'Made with AI help', changed: 'Changed with AI help', failed: 'Couldn\'t reach AI help, showing the translator result',
            keyPh: 'OpenAI API key (sk-…)', keyBad: 'Please enter an OpenAI key that starts with sk-.',
            privacy: 'Only sentences the translator doesn\'t understand (with a summary of the current blocks) are sent to OpenAI for help. The key is kept in this tab only and usage is billed to that key\'s account (about $0.001 per sentence). Ask students not to type personal information.'
        }
    }
};
// 에디터 언어 → 번역기 언어(ko/en). 그 밖의 언어는 null(패널을 숨긴다).
const uiLang = locale => (/^ko/.test(locale || '') ? 'ko' : (/^en/.test(locale || '') ? 'en' : null));

const X_ICON = '<svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>';
const MIC_ICON = '<svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="3" width="6" height="11" rx="3"/><path d="M5 11a7 7 0 0 0 14 0M12 18v3"/></svg>';
// 크롬·엣지(웹킷)만 음성 인식이 있다 — 없으면 버튼을 안 보인다
const SpeechRec = () => (typeof window === 'undefined' ? null : (window.SpeechRecognition || window.webkitSpeechRecognition || null));
const MIC_OK = 'curoai_vibe_mic'; // 마이크 안내에 동의함(이 브라우저)
const micAgreed = () => { try { return window.localStorage.getItem(MIC_OK) === '1'; } catch (e) { return false; } };
const INFO_ICON = '<svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="9"/><path d="M12 11v6M12 7.5v.5"/></svg>';
const EDIT_ICON = '<svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 20h4L19 9l-4-4L4 16z"/></svg>';
// 2026-10-16 창 다듬기(직원 의견)용 아이콘
const CHECK_ICON = '<svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>';
const WARN_ICON = '<svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><circle cx="12" cy="12" r="9"/><path d="M12 7.5v5.5M12 16.5v.5"/></svg>';
const GRIP_ICON = '<svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor"><circle cx="9" cy="6" r="1.6"/><circle cx="15" cy="6" r="1.6"/><circle cx="9" cy="12" r="1.6"/><circle cx="15" cy="12" r="1.6"/><circle cx="9" cy="18" r="1.6"/><circle cx="15" cy="18" r="1.6"/></svg>';
const UNDO_ICON = '<svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 14L4 9l5-5"/><path d="M4 9h10a6 6 0 0 1 0 12h-3"/></svg>';
const CHAT_X_ICON = '<svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 5h16v11H9l-5 4z"/><path d="M10 8.5l4 4M14 8.5l-4 4"/></svg>';
const CHEV_ICON = '<svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M9 6l6 6-6 6"/></svg>';
// 큐브로이드 로봇(하드웨어) 블록·확장. 날씨·챗GPT 같은 AI·데이터 확장도 이름이 cubroid 로 시작하지만 로봇이 아니다(2026-10-16)
const RE_ROBOT_OP = /cubroid(?:dcmotor0[12]|proximity|sound|lighttouch|led|master)_/;
const ROBOT_EXT = ['cubroiddcmotor01', 'cubroiddcmotor02', 'cubroidproximity', 'cubroidsound', 'cubroidlighttouch', 'cubroidled', 'cubroidmaster'];
const POS_KEY = 'curoai_vibe_pos'; // 학생이 옮긴 창 위치(이 브라우저)
const DETAIL_KEY = 'curoai_vibe_detail'; // 자세히 보기 펼침(이 브라우저)
const store = {
    get: k => { try { return window.localStorage.getItem(k); } catch (e) { return null; } },
    set: (k, v) => { try { if (v === null) window.localStorage.removeItem(k); else window.localStorage.setItem(k, v); } catch (e) { /* 막힌 환경 */ } }
};
const icon = (html, style) => (
    <span
        aria-hidden="true"
        dangerouslySetInnerHTML={{__html: html}} // eslint-disable-line react/no-danger
        style={Object.assign({display: 'inline-flex', lineHeight: 0}, style)}
    />
);

// 2026-10-16 직원 의견 "기능별 구역 구분이 안 되고 모두 같은 크기·색이라 읽기 어렵다" → 구역마다 다른 바탕·글씨로 나눈다.
//   머리(끌어서 옮김) | 대화(옅은 바탕) | 입력·버튼 | 예시 | 자세히(접힘) | AI 도움(바닥)
// 되돌리기: git(scratch-gui 108dd05 의 vibe-panel.jsx). 기능 로직은 그대로라 이 S 와 render 만 바꾸면 예전 모양.
const S = {
    pill: {
        position: 'absolute', right: '4.5rem', bottom: '0.9rem', zIndex: 40,
        display: 'flex', alignItems: 'center', gap: '0.4rem',
        padding: '0.55rem 1rem', borderRadius: '999px', border: 'none', cursor: 'pointer',
        background: '#4C97FF', color: '#fff', fontSize: '0.9rem', fontWeight: 700,
        boxShadow: '0 4px 14px rgba(76,151,255,0.35)'
    },
    card: {
        position: 'absolute', right: '4.5rem', bottom: '0.9rem', zIndex: 40,
        width: 'min(420px, calc(100% - 22rem))', minWidth: '300px', maxHeight: 'calc(100% - 1.8rem)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        background: '#fff', borderRadius: '14px', border: '1px solid #d5deeb',
        boxShadow: '0 10px 30px rgba(30,50,90,0.18)', color: '#2b3445',
        fontSize: '0.85rem', lineHeight: 1.5
    },
    head: {
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', flex: 'none',
        padding: '0.5rem 0.6rem 0.5rem 0.55rem', background: '#f2f6fc', borderBottom: '1px solid #e1e8f2',
        cursor: 'move', userSelect: 'none', touchAction: 'none'
    },
    title: {fontSize: '0.98rem', fontWeight: 800, color: '#1f3b73', display: 'flex', alignItems: 'center', gap: '0.3rem'},
    grip: {color: '#a9b4c6', fontSize: '1rem'},
    // 대화 칸 위 이름 줄: 왼쪽 「대화」, 오른쪽 「대화 지우기」(말풍선과 겹치지 않게 칸 밖에)
    logHead: {display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.3rem'},
    logClear: {
        border: '1px solid #d5deeb', background: '#fff', borderRadius: '999px', padding: '0.1rem 0.5rem',
        fontSize: '0.72rem', color: '#4a5568', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.25rem'
    },
    close: {border: 'none', background: 'transparent', cursor: 'pointer', color: '#8b95a7', fontSize: '1rem', padding: '0.25rem', display: 'flex'},
    body: {padding: '0.7rem 0.85rem 0.75rem', overflowY: 'auto', flex: '1 1 auto', minHeight: 0},
    sub: {color: '#6b7689', fontSize: '0.8rem', marginBottom: '0.5rem'},
    log: {
        display: 'flex', flexDirection: 'column', gap: '0.35rem', marginBottom: '0.6rem', maxHeight: '10.5rem', overflowY: 'auto',
        padding: '0.5rem', background: '#f5f7fb', borderRadius: '10px'
    },
    me: {alignSelf: 'flex-end', background: '#4C97FF', color: '#fff', borderRadius: '10px 10px 2px 10px', padding: '0.22rem 0.6rem', maxWidth: '85%', fontSize: '0.84rem'},
    bot: {
        alignSelf: 'flex-start', display: 'flex', alignItems: 'flex-start', gap: '0.3rem', maxWidth: '92%',
        background: '#fff', border: '1px solid #e1e8f2', color: '#2f3a4d', borderRadius: '10px 10px 10px 2px',
        padding: '0.2rem 0.55rem', fontSize: '0.8rem'
    },
    botWarn: {background: '#fff7eb', borderColor: '#f3d6a4', color: '#7a4a00'},
    input: {
        width: '100%', boxSizing: 'border-box', resize: 'none', border: '1.5px solid #c9d5e8', borderRadius: '10px',
        padding: '0.55rem 0.7rem', fontSize: '0.9rem', fontFamily: 'inherit', outline: 'none', color: '#1f2633'
    },
    row: {display: 'flex', gap: '0.45rem', marginTop: '0.5rem'},
    primary: {
        flex: 1, border: 'none', borderRadius: '9px', padding: '0.5rem', cursor: 'pointer',
        background: '#4C97FF', color: '#fff', fontWeight: 700, fontSize: '0.88rem'
    },
    secondary: {
        border: '1px solid #c9d5e8', borderRadius: '9px', padding: '0.5rem 0.65rem', cursor: 'pointer', whiteSpace: 'nowrap',
        background: '#fff', color: '#4a5568', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.3rem'
    },
    chips: {display: 'flex', flexWrap: 'wrap', gap: '0.35rem'},
    chip: {
        border: '1px solid #dbe3ef', background: '#f5f8fd', color: '#3d4a60', borderRadius: '999px',
        padding: '0.2rem 0.6rem', fontSize: '0.75rem', cursor: 'pointer'
    },
    pick: {
        border: '1px solid #b8cdef', background: '#fff', color: '#1f3b73', borderRadius: '8px',
        padding: '0.15rem 0.55rem', fontSize: '0.78rem', cursor: 'pointer', fontWeight: 700
    },
    askInput: {border: '1px solid #b8cdef', borderRadius: '8px', padding: '0.15rem 0.45rem', fontSize: '0.8rem', width: '9rem'},
    section: {marginTop: '0.75rem'},
    // 구역 이름: 본문과 다른 색·굵기·크기로(직원 의견: 모두 같은 크기·색이라 구분이 안 됨)
    label: {fontSize: '0.74rem', fontWeight: 800, color: '#3a6fc4', marginBottom: '0.3rem'},
    box: {padding: '0.5rem 0.65rem', background: '#f5f8fd', border: '1px solid #e1e8f2', borderRadius: '10px'},
    detailBtn: {
        border: 'none', background: 'transparent', color: '#4a5f86', fontSize: '0.78rem', fontWeight: 700,
        cursor: 'pointer', padding: '0.15rem 0', display: 'flex', alignItems: 'center', gap: '0.25rem', marginTop: '0.7rem'
    },
    detailBox: {marginTop: '0.3rem', padding: '0.1rem 0.7rem 0.55rem', background: '#fafbfd', border: '1px solid #edf1f7', borderRadius: '10px'},
    dsec: {marginTop: '0.55rem'},
    dlabel: {fontSize: '0.72rem', fontWeight: 700, color: '#7a869a', marginBottom: '0.2rem'},
    echo: {fontSize: '0.92rem', lineHeight: 1.9, wordBreak: 'keep-all'},
    step: {display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.1rem 0'},
    dot: {width: '0.6rem', height: '0.6rem', borderRadius: '3px', flex: 'none'},
    note: {display: 'flex', gap: '0.35rem', color: '#4a5568', fontSize: '0.8rem', padding: '0.15rem 0'},
    change: {display: 'flex', gap: '0.35rem', color: '#1f3b73', fontSize: '0.82rem', padding: '0.15rem 0', fontWeight: 600},
    err: {marginTop: '0.55rem', color: '#b4441c', fontSize: '0.8rem'},
    legend: {display: 'flex', flexWrap: 'wrap', gap: '0.6rem', marginTop: '0.35rem', color: '#7a869a', fontSize: '0.7rem'},
    foot: {flex: 'none', padding: '0.4rem 0.85rem', borderTop: '1px solid #edf1f7', background: '#fafbfd', fontSize: '0.75rem', color: '#6b7689'}
};

class VibePanel extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, ['handleOpen', 'handleClose', 'handleChange', 'handleKeyDown', 'handleMake', 'handleUndo',
            'handleChip', 'handleAsk', 'handleAskText', 'handleAskKey', 'setInput',
            'handleAiToggle', 'handleAiKey', 'handleAiKeyInput', 'handleAiKeyDown', 'handleMic', 'handleMicOk', 'handleMicCancel',
            'handleClearLog', 'handleDetail', 'handleDragStart', 'handleDragMove', 'handleDragEnd', 'handleDragReset', 'handleResize',
            'setCard', 'setLogBox']);
        // cur = 지금 VM 에 들어가 있는 결과 {ir, applied} · history = 고쳐 말하기 전 버전들("되돌려" = 한 단계 전)
        // aiOn = AI 도움 켜짐(키도 있음) · aiSetup = 키 넣는 칸 열림 · aiBusy = 응답 기다리는 중
        // pos = 학생이 끌어 옮긴 창 위치 {left, top}(px, 블록 작업 영역 기준) · null 이면 오른쪽 아래 기본 자리
        // detail = "자세히 보기" 펼침(알아들은 말·지금 블록·알림). 2026-10-16 직원 의견으로 기본은 접힘
        let pos = null;
        try { pos = JSON.parse(store.get(POS_KEY) || 'null'); } catch (e) { pos = null; }
        this.state = {open: false, text: '', cur: null, history: [], view: null, error: null, log: [], askText: {}, help: null,
            aiOn: aiReady(), aiSetup: false, aiKey: '', aiBusy: false,
            chips: [], explain: null, listening: false, micAsk: false, // chips = 지금 만든 것에 맞춘 예시(되는 것만) · explain = "어떻게 동작해?" 답
            pos: pos && typeof pos.left === 'number' && typeof pos.top === 'number' ? pos : null,
            detail: store.get(DETAIL_KEY) === '1'};
    }
    componentDidMount () {
        window.addEventListener('resize', this.handleResize);
    }
    componentDidUpdate (prevProps, prevState) {
        // 창을 열 때·창 크기가 바뀌었을 때 옮겨 둔 자리가 영역 밖이면 안으로 당긴다
        if (this.state.open && !prevState.open && this.state.pos) this.handleResize();
        // 대화가 늘면 맨 아래(방금 말)가 보이게
        if (this.logBox && prevState.log !== this.state.log) this.logBox.scrollTop = this.logBox.scrollHeight;
    }
    componentWillUnmount () {
        if (this.rec) { try { this.rec.abort(); } catch (e) { /* 이미 끝남 */ } }
        window.removeEventListener('resize', this.handleResize);
        this.handleDragEnd();
    }
    setCard (el) {
        this.card = el;
    }
    setLogBox (el) {
        this.logBox = el;
    }
    // ── 창 옮기기(2026-10-16 직원 의견 "팝업창이 자유롭게 이동되게. 창을 가려서 불편함") ──
    // 머리줄을 끌면 블록 작업 영역 안에서 움직인다. 두 번 누르면 원래 자리(오른쪽 아래). 자리는 이 브라우저에 기억.
    clampPos (left, top) {
        const card = this.card;
        const parent = card && card.offsetParent;
        if (!parent) return null;
        const maxLeft = Math.max(0, parent.clientWidth - card.offsetWidth);
        const headH = this.headH || 44;
        const maxTop = Math.max(0, parent.clientHeight - headH);
        return {left: Math.round(Math.min(Math.max(left, 0), maxLeft)), top: Math.round(Math.min(Math.max(top, 0), maxTop))};
    }
    handleDragStart (e) {
        if (e.button !== 0) return;
        if (e.target && e.target.closest && e.target.closest('button')) return; // 대화 지우기·닫기 버튼은 그대로 눌리게
        const card = this.card;
        const parent = card && card.offsetParent;
        if (!parent) return;
        const cr = card.getBoundingClientRect();
        const pr = parent.getBoundingClientRect();
        this.headH = e.currentTarget.offsetHeight;
        this.drag = {dx: e.clientX - cr.left, dy: e.clientY - cr.top, pl: pr.left, pt: pr.top};
        window.addEventListener('pointermove', this.handleDragMove);
        window.addEventListener('pointerup', this.handleDragEnd);
        window.addEventListener('pointercancel', this.handleDragEnd);
        e.preventDefault();
    }
    handleDragMove (e) {
        const d = this.drag;
        if (!d) return;
        const pos = this.clampPos(e.clientX - d.pl - d.dx, e.clientY - d.pt - d.dy);
        if (pos) this.setState({pos});
    }
    handleDragEnd () {
        if (!this.drag) return;
        this.drag = null;
        window.removeEventListener('pointermove', this.handleDragMove);
        window.removeEventListener('pointerup', this.handleDragEnd);
        window.removeEventListener('pointercancel', this.handleDragEnd);
        if (this.state.pos) store.set(POS_KEY, JSON.stringify(this.state.pos));
    }
    handleDragReset (e) {
        if (e && e.target && e.target.closest && e.target.closest('button')) return;
        store.set(POS_KEY, null);
        this.setState({pos: null});
    }
    handleResize () {
        if (!this.state.pos || !this.card) return;
        const pos = this.clampPos(this.state.pos.left, this.state.pos.top);
        if (pos && (pos.left !== this.state.pos.left || pos.top !== this.state.pos.top)) this.setState({pos});
    }
    // ── 대화 지우기(2026-10-16 직원 의견 "로그 리셋 버튼 필요") ──
    // 대화 기록만 지운다. 만든 블록은 작업 영역에 그대로 두고, 다음 말은 고쳐 말하기가 아니라 새로 만들기로 듣는다.
    handleClearLog () {
        this.setState({log: [], cur: null, history: [], view: null, error: null, help: null, explain: null, chips: [], askText: {}, text: ''},
            () => { if (this.input) this.input.focus(); });
    }
    handleDetail () {
        const detail = !this.state.detail;
        store.set(DETAIL_KEY, detail ? '1' : '0');
        this.setState({detail});
    }
    // ── 말로 입력(마이크) — 처음 한 번 안내·동의, 그다음부터 누르면 바로 듣는다. 다 들으면 그 문장으로 바로 만든다 ──
    handleMic () {
        if (this.state.listening) { if (this.rec) this.rec.stop(); return; }
        if (!micAgreed()) { this.setState({micAsk: true}); return; }
        this.startMic();
    }
    handleMicOk () {
        try { window.localStorage.setItem(MIC_OK, '1'); } catch (e) { /* 막힌 환경 — 이번만 */ }
        this.setState({micAsk: false}, () => this.startMic());
    }
    handleMicCancel () {
        this.setState({micAsk: false});
    }
    startMic () {
        const SR = SpeechRec();
        if (!SR || this.state.aiBusy) return;
        const M = this.ui().mic;
        const rec = new SR();
        rec.lang = this.lang() === 'en' ? 'en-US' : 'ko-KR';
        rec.interimResults = true;
        rec.continuous = false;
        rec.maxAlternatives = 1;
        this.rec = rec;
        this.micFinal = '';
        rec.onresult = ev => {
            let all = '';
            let fin = '';
            for (let i = 0; i < ev.results.length; i++) {
                all += ev.results[i][0].transcript;
                if (ev.results[i].isFinal) fin += ev.results[i][0].transcript;
            }
            if (fin) this.micFinal = fin.trim();
            this.setState({text: all.trim()});
        };
        rec.onerror = ev => {
            const e = ev && ev.error;
            this.micError = e === 'not-allowed' || e === 'service-not-allowed' ? M.denied : e === 'no-speech' ? M.none : e === 'network' ? M.net : null;
        };
        rec.onend = () => {
            this.rec = null;
            const t = this.micFinal;
            const err = this.micError;
            this.micFinal = '';
            this.micError = null;
            this.setState({listening: false}, () => {
                if (t) { this.setState({text: t}, () => this.handleMake()); return; }
                if (err) this.setState({error: err});
            });
        };
        try {
            rec.start();
            this.setState({listening: true, error: null, text: ''});
        } catch (e) {
            this.rec = null;
            this.setState({listening: false});
        }
    }
    // 지금 스프라이트 기준 번역 ctx
    ctxNow () {
        const vm = this.props.vm;
        const target = vm.editingTarget;
        const stage = vm.runtime.getTargetForStage ? vm.runtime.getTargetForStage() : null;
        return {
            lang: this.lang(),
            soundNames: target && target.getSounds ? target.getSounds().map(x => x.name) : [],
            costumeNames: target && target.getCostumes ? target.getCostumes().map(c => c.name) : [],
            backdropNames: stage && stage.getCostumes ? stage.getCostumes().map(c => c.name) : []
        };
    }
    // 이 프로젝트가 로봇 프로젝트인가: 로봇 확장이 추가돼 있거나, 지금 스프라이트에 로봇 블록이 있다
    robotProject () {
        const vm = this.props.vm;
        try {
            const em = vm.extensionManager;
            if (em && ROBOT_EXT.some(id => em.isExtensionLoaded(id))) return true;
            const t = vm.editingTarget;
            const b = t && t.blocks && t.blocks._blocks;
            return Boolean(b && Object.keys(b).some(k => RE_ROBOT_OP.test(String(b[k].opcode))));
        } catch (e) {
            return false;
        }
    }
    // 만든 뒤 "다음에 말해 볼 것" — 화면을 먼저 그리고 나서 계산(고쳐 말하기를 몇 번 돌려 되는 것만 남긴다)
    refreshChips (ir) {
        setTimeout(() => {
            const cur = this.liveCur();
            if (!cur || cur.ir !== ir) return;
            let chips = [];
            try {
                const robot = RE_ROBOT_OP.test(JSON.stringify(ir.scripts));
                chips = suggestChips(ir, Object.assign(this.ctxNow(), robot ? {robot: true} : (this.robotProject() ? {robotHint: true} : {})));
            } catch (e) { chips = []; }
            this.setState({chips});
        }, 0);
    }
    // ── AI 도움(선생님 키) ──
    handleAiToggle () {
        if (this.state.aiOn) { aiSetOn(false); this.setState({aiOn: false, aiSetup: false}); return; }
        if (/^sk-/.test(aiGetKey())) { aiSetOn(true); this.setState({aiOn: true, aiSetup: false}); return; }
        this.setState({aiSetup: !this.state.aiSetup, aiKey: ''});
    }
    handleAiKeyInput (e) {
        this.setState({aiKey: e.target.value});
    }
    handleAiKeyDown (e) {
        if (e.key === 'Enter') { e.preventDefault(); this.handleAiKey(); }
    }
    handleAiKey () {
        const k = this.state.aiKey.trim();
        if (!/^sk-/.test(k)) { this.setState({error: this.ui().ai.keyBad}); return; }
        aiSetKey(k);
        aiSetOn(true);
        this.setState({aiOn: true, aiSetup: false, aiKey: '', error: null});
    }
    // 규칙이 못 알아들었을 때 AI 에 묻는다. 실패하면 fallback()(규칙 결과)으로.
    askAi (text, ctx, curIr, fallback) {
        const L = this.ui();
        this.setState({aiBusy: true, error: null, log: this.pushLog(text, L.ai.thinking)});
        aiHelp(text, ctx, curIr).then(res => {
            const log = this.state.log.slice(0, -1); // "생각하는 중" 줄을 바꾼다
            if (res.ir) {
                if (curIr && this.liveCur()) {
                    this.setState({aiBusy: false, log}, () => this.replaceWith(res.ir, {text, changes: [L.ai.changed], notes: res.ir.notes}, true));
                    return;
                }
                this.setState({aiBusy: false, log}, () => this.applyNew(text, res.ir, L.ai.made));
                return;
            }
            if (res.message) { this.setState({aiBusy: false, log: log.concat([{me: text, bot: res.message, warn: true}]).slice(-4), error: res.message, text: ''}); return; }
            this.setState({aiBusy: false, log}, () => fallback(`${L.ai.failed} (${res.error})`));
        });
    }
    // 새로 만든 결과를 VM 에 넣고 화면을 채운다(규칙·AI 공통)
    applyNew (text, ir, summaryOverride) {
        const L = this.ui();
        const applied = applyToVm(this.props.vm, ir);
        if (applied.error) { this.setState({error: L.err[applied.error] || applied.error}); return; }
        const rows = describe(ir);
        const count = rows.reduce((a, r) => a + r.length, 0);
        this.setState({
            cur: ir.scripts.length ? {ir, applied} : null, history: [],
            view: {echo: ir.segments && ir.segments.length ? {text: ir.text, segments: ir.segments} : null, rows, notes: ir.notes.concat(applied.notes || []), changes: [], asks: ir.asks || [], unparsed: ir.unparsed.length},
            error: null, help: null, explain: null, text: '', chips: [],
            log: this.pushLog(text, summaryOverride || (count ? L.made(count, ir.unparsed.length) : L.onlyNotes), !summaryOverride && !count)
        }, () => { if (ir.scripts.length) this.refreshChips(ir); });
    }
    handleOpen () {
        this.setState({open: true}, () => { if (this.input) this.input.focus(); });
    }
    handleClose () {
        this.setState({open: false});
    }
    handleChange (e) {
        this.setState({text: e.target.value});
    }
    handleKeyDown (e) {
        // 한글 입력 중(조합 중) Enter 는 글자를 확정하는 것이지 제출이 아니다 — 안 막으면 두 번 만들어진다
        if (e.key === 'Enter' && !e.shiftKey && !(e.nativeEvent && e.nativeEvent.isComposing) && e.keyCode !== 229) {
            e.preventDefault();
            this.handleMake();
        }
    }
    handleChip (e) {
        this.setState({text: e.currentTarget.getAttribute('data-text'), error: null}, () => { if (this.input) this.input.focus(); });
    }
    // warn = 블록을 못 만들었거나 못 고친 답(주황 말풍선으로 구분, 2026-10-16)
    pushLog (text, summary, warn) {
        const row = text ? {me: text, bot: summary} : {bot: summary};
        if (warn) row.warn = true;
        return this.state.log.concat([row]).slice(-4);
    }
    // 지금 결과가 여전히 편집 중인 스프라이트의 것인지(스프라이트를 바꿨으면 새로 시작)
    liveCur () {
        const cur = this.state.cur;
        const t = this.props.vm.editingTarget;
        return cur && t && cur.applied && cur.applied.targetId === t.id ? cur : null;
    }
    // 방금 넣은 블록을 지우고 새 버전을 넣는다(고쳐 말하기·되묻기·되돌리기 공통)
    replaceWith (ir, meta, pushHistory) {
        const vm = this.props.vm;
        const L = this.ui();
        const cur = this.liveCur();
        const u = undoApply(vm, cur.applied);
        if (u.error) {
            this.setState({error: L.err[u.error] || u.error, cur: null, history: [], text: '', log: this.pushLog(meta.text, L.err[u.error], true)});
            return;
        }
        const applied = applyToVm(vm, ir);
        const summary = meta.changes && meta.changes.length ?
            `${meta.changes[0]}${meta.changes.length > 1 ? L.more(meta.changes.length - 1) : ''}` : L.changed;
        this.setState({
            cur: {ir, applied},
            history: pushHistory ? this.state.history.concat([cur.ir]).slice(-10) : this.state.history,
            view: {echo: meta.echo || null, rows: describe(ir), notes: (meta.notes || []).concat(applied.notes || []), changes: meta.changes || [], asks: ir.asks || [], unparsed: ir.unparsed.length},
            error: null, help: null, explain: null, text: '', log: this.pushLog(meta.text, summary), chips: []
        }, () => this.refreshChips(ir));
    }
    handleMake () {
        const text = this.state.text.trim();
        if (!text) return;
        const vm = this.props.vm;
        const target = vm.editingTarget;
        const L = this.ui();
        if (!target || target.isStage) { this.setState({error: L.err.stage}); return; }
        const soundNames = (target.getSounds ? target.getSounds() : []).map(s => s.name);
        const stage = vm.runtime.getTargetForStage ? vm.runtime.getTargetForStage() : null;
        const ctx = {
            lang: this.lang(),
            soundNames,
            costumeNames: (target.getCostumes ? target.getCostumes() : []).map(c => c.name),
            backdropNames: (stage && stage.getCostumes ? stage.getCostumes() : []).map(c => c.name)
        };
        if (this.state.aiBusy) return; // AI 답을 기다리는 중에는 새 말을 받지 않는다(두 번 만들어지지 않게)
        // 학생이 방금 만든 블록을 직접 고쳤으면 — 고친 블록은 학생 것으로 두고 이번 말은 새로 만들기로 듣는다
        // (2026-10-06 직원 실측: 고친 뒤로는 무슨 말을 해도 "직접 고쳤네요"만 나와 멈춘 것처럼 보였다)
        const cur0 = this.liveCur();
        const edited = Boolean(cur0 && !isIntact(vm, cur0.applied));
        if (edited) this.setState({cur: null, history: [], view: null, chips: []}); // 고치기 전 "지금 블록" 목록이 남아 있으면 헷갈린다
        const cur = edited ? null : cur0;
        const ai = this.state.aiOn && aiReady();
        // "이거 어떻게 동작해?", "설명해줘" — 방금 만든 블록을 한 줄씩 쉬운 말로(블록은 안 바꾼다)
        if (cur && isExplainRequest(text, this.lang())) {
            const ex = explainIr(cur.ir, this.lang());
            this.setState({explain: ex, help: null, error: null, text: '', log: this.pushLog(text, ex.title)});
            return;
        }
        if (cur) {
            // 바이브 루프 — 방금 만든 것을 고치는 말인가?
            const r = refine(cur.ir, text, ctx);
            if (r && r.kind === 'undo') { this.undo(text); return; }
            // "얼굴 인식해서 웃으면 ~", "서울 기온 말해" — 아직 말로 못 만드는 AI·데이터 확장인데 그 블록이 없으면: 틀린 블록 대신 안내
            const guard = aiExtGuard(text, this.lang(), r && r.ir ? r.ir : null);
            if (guard && r && r.ir) { this.setState({help: guard, error: null, text: '', log: this.pushLog(text, guard.text)}); return; }
            // 고치는 말로 못 알아들은 AI·데이터 확장 말("웃으면 점프해")은 아래에서 새 문장으로 번역해 본다(지킴이는 그 결과로 다시 본다)
            if (!guard) {
                // "사운드 블록 넣어보자" — 확장 이름만 부르고 할 일이 없는 말: 그 확장으로 할 수 있는 말을 보여 준다(AI 보다 먼저, 추측하지 않음)
                if (r && !r.ir && r.fallback) {
                    const eh = extensionHelp(text, this.lang(), cur.ir);
                    if (eh) { this.setState({help: eh, error: null, text: '', log: this.pushLog(text, eh.text)}); return; }
                }
                // 규칙이 "어떻게 바꿀지 모르겠어요"로 끝났으면 — AI 도움(켜져 있을 때만)
                if (r && !r.ir && r.fallback && ai) {
                    this.askAi(text, ctx, cur.ir, err => this.setState({error: err ? `${r.message} · ${err}` : r.message, text: '', log: this.pushLog(text, r.message, true)}));
                    return;
                }
                if (r && !r.ir) { this.setState({error: r.message, text: '', log: this.pushLog(text, r.message, true)}); return; }
                if (r) { this.replaceWith(r.ir, {text, changes: r.changes, notes: r.notes, echo: r.echo}, true); return; }
            }
        }
        // 방금 만든 게 로봇이면 새 말도 로봇 이야기로 듣는다("로봇 뒤로 1초" 다음 "앞으로 1초 가고 삐 소리")
        // 2026-10-16 직원 의견 "알아들을 때도 있고 못 알아들을 때도": 방금 만든 것만 보면 대화를 지우거나 새로 시작할 때 로봇 문맥이 사라졌다
        // → 프로젝트에 로봇 블록·확장이 있으면 약한 힌트(robotHint). 번역기는 고양이·스프라이트를 말하면 힌트를 무시한다.
        const robotCtx = Boolean(cur0 && RE_ROBOT_OP.test(JSON.stringify(cur0.ir.scripts)));
        const ctx2 = robotCtx ? Object.assign({}, ctx, {robot: true}) : (this.robotProject() ? Object.assign({}, ctx, {robotHint: true}) : ctx);
        const ir = translate(text, ctx2);
        // 고친 블록에 "더 빨리"처럼 고치는 말만 했으면 새로 만들 게 없다 — 주석·빈 결과 대신 처음부터 말해 달라고
        if (edited && !ir.scripts.length) { this.setState({error: L.err.changed, text: '', log: this.pushLog(text, L.err.changed, true)}); return; }
        // 질문("사운드 블록도 사용 가능해?")인데 만들 블록이 없으면 — 블록·주석 없이 할 수 있는 말을 알려 준다
        const guard2 = aiExtGuard(text, this.lang(), ir);
        if (guard2) {
            const showGuard = () => this.setState({help: guard2, error: null, text: '', log: this.pushLog(text, guard2.text)});
            // AI 도움이 켜져 있으면 먼저 AI 에게(AI 도 이제 날씨·차트·카메라 블록을 안다, 2026-09-28) — 못 하면 안내 상자
            if (ai) { this.askAi(text, ctx2, cur ? cur.ir : null, () => showGuard()); return; }
            showGuard();
            return;
        }
        const answer = answerQuestion(text, this.lang());
        // "왜 안 움직여?", "모터 한쪽만 돌아요 왜그래요" — 분명한 질문이면 블록보다 답이 먼저
        if (answer && (!ir.scripts.length || answer.strong)) {
            this.setState({help: answer, error: null, text: '', log: this.pushLog(text, answer.text)});
            return;
        }
        // 규칙이 못 알아들었는데 확장 이름("LED 블록 써보자")이면 — 도움 상자(예시를 누르면 입력칸에 들어감)
        if (ir.unparsed.length || !ir.scripts.length) {
            const eh = extensionHelp(text, this.lang(), cur ? cur.ir : null);
            if (eh) { this.setState({help: eh, error: null, text: '', log: this.pushLog(text, eh.text)}); return; }
        }
        const ruleDone = () => {
            if (!ir.scripts.length && !ir.unparsed.length) {
                this.setState({error: L.err.empty, log: this.pushLog(text, L.nothing, true)});
                return;
            }
            this.applyNew(text, ir, edited && ir.scripts.length ? L.madeKeep(describe(ir).reduce((a, r) => a + r.length, 0)) : null);
        };
        // 규칙이 일부라도 못 알아들었으면(모르는 말·빈 결과) — AI 도움(켜져 있을 때만). 고쳐 말하던 중이면 지금 블록을 함께 보낸다.
        if (ai && (ir.unparsed.length || !ir.scripts.length)) {
            this.askAi(text, ctx2, cur ? cur.ir : null, err => { ruleDone(); if (err) this.setState({error: err}); });
            return;
        }
        ruleDone();
    }
    // "되돌려" — 고쳐 말한 게 있으면 한 단계 전으로, 없으면 방금 만든 블록을 지운다
    undo (text) {
        const cur = this.liveCur();
        if (!cur) return;
        const hist = this.state.history;
        if (hist.length) {
            const prevIr = hist[hist.length - 1];
            this.setState({history: hist.slice(0, -1)}, () => {
                this.replaceWith(prevIr, {text, changes: [this.ui().undoStep]}, false);
            });
            return;
        }
        const L = this.ui();
        const u = undoApply(this.props.vm, cur.applied);
        if (u.error) { this.setState({error: L.err[u.error], cur: null, history: [], log: this.pushLog(text, L.err[u.error], true)}); return; }
        this.setState({cur: null, history: [], view: null, error: null, explain: null, chips: [], text: '', log: this.pushLog(text, L.removed)});
    }
    handleUndo () {
        this.undo(null);
    }
    // 되묻기 — 번역기가 대신 정한 값을 학생이 고른다
    handleAsk (e) {
        const id = Number(e.currentTarget.getAttribute('data-id'));
        this.answerAsk(id, e.currentTarget.getAttribute('data-value'));
    }
    handleAskText (e) {
        const id = e.currentTarget.getAttribute('data-id');
        this.setState({askText: Object.assign({}, this.state.askText, {[id]: e.target.value})});
    }
    handleAskKey (e) {
        if (e.key !== 'Enter' || (e.nativeEvent && e.nativeEvent.isComposing) || e.keyCode === 229) return;
        e.preventDefault();
        const id = e.currentTarget.getAttribute('data-id');
        const v = (this.state.askText[id] || '').trim();
        if (v) this.answerAsk(Number(id), v);
    }
    answerAsk (id, value) {
        const cur = this.liveCur();
        if (!cur) return;
        const r = setAsk(cur.ir, id, value);
        if (!r) return;
        this.replaceWith(r.ir, {text: null, changes: r.changes}, true);
    }
    setInput (el) {
        this.input = el;
    }
    lang () {
        return uiLang(this.props.intl && this.props.intl.locale) || 'ko';
    }
    ui () {
        return UI[this.lang()];
    }
    renderEcho (echo) {
        const out = [];
        let pos = 0;
        (echo.segments || []).forEach((sg, i) => {
            if (sg.start > pos) out.push(<span key={`p${i}`}>{echo.text.slice(pos, sg.start)}</span>);
            const piece = echo.text.slice(sg.start, sg.end);
            if (sg.cat === 'unparsed') {
                out.push(
                    <span
                        key={`s${i}`}
                        style={{color: '#8a8f99', borderBottom: '2px dashed #b9bec8'}}
                        title={this.ui().unknownTip}
                    >{piece}</span>
                );
            } else {
                const c = CAT[sg.cat] || '#999';
                out.push(
                    <span
                        key={`s${i}`}
                        style={{background: `${c}26`, borderBottom: `2.5px solid ${c}`, borderRadius: '3px', padding: '0 1px'}}
                        title={this.ui().cats[sg.cat]}
                    >{piece}</span>
                );
            }
            pos = sg.end;
        });
        if (pos < echo.text.length) out.push(<span key="tail">{echo.text.slice(pos)}</span>);
        return out;
    }
    renderAsks (asks) {
        if (!asks || !asks.length) return null;
        return (
            <div style={S.section}>
                <div style={S.label}>{this.ui().pickTitle}</div>
                <div style={S.box}>
                    {asks.map(a => (
                        <div
                            key={a.id}
                            style={{display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.35rem', padding: '0.1rem 0'}}
                        >
                            <span style={{color: '#4a5568'}}>{a.q}</span>
                            {a.options ? a.options.map(v => (
                                <button
                                    data-id={a.id}
                                    data-value={v}
                                    key={v}
                                    style={Object.assign({}, S.pick, String(v) === String(a.current) ? {background: '#eaf2ff'} : {})}
                                    onClick={this.handleAsk}
                                >{String(v)}</button>
                            )) : (
                                <input
                                    data-id={a.id}
                                    placeholder={this.ui().now(a.current)}
                                    style={S.askInput}
                                    value={this.state.askText[a.id] || ''}
                                    onChange={this.handleAskText}
                                    onKeyDown={this.handleAskKey}
                                />
                            )}
                        </div>
                    ))}
                </div>
            </div>
        );
    }
    // 2026-10-16 직원 의견 "꼭 필요한 정보만, 기능 설명은 최대한 줄여" → 알아들은 말·지금 블록·바꾼 것·알림은 「자세히 보기」 안으로(기본 접힘).
    // 블록은 작업 영역에 이미 보이므로 창에는 대화·입력·예시만 둔다. 펼침 여부는 이 브라우저에 기억.
    renderView () {
        const v = this.state.view;
        if (!v) return null;
        const L = this.ui();
        const hasEcho = Boolean(v.echo && v.echo.segments && v.echo.segments.length);
        if (!hasEcho && !v.rows.length && !(v.notes && v.notes.length) && !v.changes.length) return null;
        const usedCats = hasEcho ? Array.from(new Set((v.echo.segments || []).map(s => s.cat).filter(c => CAT[c]))) : [];
        const open = this.state.detail;
        const nNotes = v.notes ? v.notes.length : 0;
        return (
            <div>
                <button
                    aria-expanded={open}
                    style={S.detailBtn}
                    onClick={this.handleDetail}
                >
                    {icon(CHEV_ICON, {transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s'})}
                    {open ? L.detailHide : L.detailShow}
                    {nNotes ? <span style={{fontWeight: 400, color: '#7a869a'}}>{L.detailNotes(nNotes)}</span> : null}
                </button>
                {open ? (
                    <div style={S.detailBox}>
                        {v.changes.length ? (
                            <div style={S.dsec}>
                                <div style={S.dlabel}>{L.secChanges}</div>
                                {v.changes.map((c, i) => (
                                    <div
                                        key={i}
                                        style={S.change}
                                    >{icon(EDIT_ICON, {color: '#4C97FF', marginTop: '0.2rem'})}<span>{c}</span></div>
                                ))}
                            </div>
                        ) : null}
                        {hasEcho ? (
                            <div style={S.dsec}>
                                <div style={S.dlabel}>{L.secEcho}</div>
                                <div style={S.echo}>{this.renderEcho(v.echo)}</div>
                                <div style={S.legend}>
                                    {usedCats.map(c => (
                                        <span
                                            key={c}
                                            style={{display: 'flex', alignItems: 'center', gap: '0.25rem'}}
                                        >
                                            <span style={Object.assign({}, S.dot, {background: CAT[c]})} />{L.cats[c]}
                                        </span>
                                    ))}
                                    {v.unparsed ? <span>{L.dashed}</span> : null}
                                </div>
                            </div>
                        ) : null}
                        {v.rows.length ? (
                            <div style={S.dsec}>
                                <div style={S.dlabel}>{L.secBlocks}</div>
                                {v.rows.map((rows, si) => (
                                    <div
                                        key={si}
                                        style={{marginBottom: '0.3rem'}}
                                    >
                                        {rows.map((row, i) => (
                                            <div
                                                key={i}
                                                style={Object.assign({}, S.step, {paddingLeft: `${row.depth * 1.1}rem`})}
                                            >
                                                <span style={Object.assign({}, S.dot, {background: CAT[row.cat] || '#999'})} />
                                                <span>{row.label}</span>
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        ) : null}
                        {nNotes ? (
                            <div style={S.dsec}>
                                <div style={S.dlabel}>{L.secNotes}</div>
                                {v.notes.map((n, i) => (
                                    <div
                                        key={i}
                                        style={S.note}
                                    >
                                        {icon(INFO_ICON, {color: '#4C97FF', marginTop: '0.2rem'})}
                                        <span>{n}</span>
                                    </div>
                                ))}
                            </div>
                        ) : null}
                    </div>
                ) : null}
            </div>
        );
    }
    render () {
        if (!uiLang(this.props.intl && this.props.intl.locale)) return null; // 번역기는 한국어·영어만 알아듣는다
        const L = this.ui();
        if (!this.state.open) {
            return (
                <button
                    aria-label={L.open}
                    style={S.pill}
                    onClick={this.handleOpen}
                >
                    {icon(ICONS.spark)}{L.title}
                </button>
            );
        }
        const cur = this.liveCur();
        const chips = cur ? (this.state.chips.length ? this.state.chips : L.chips) : L.examples;
        const micOn = Boolean(SpeechRec());
        const log = this.state.log;
        // 같은 말이 바로 위 대화 말풍선에 이미 있으면 빨간 줄로 또 쓰지 않는다(같은 글이 두 번 보이던 것)
        const lastBot = log.length ? log[log.length - 1].bot : null;
        const showErr = this.state.error && this.state.error !== lastBot;
        // 버튼 이름 = 지금 누르면 일어나는 일(고쳐 말한 게 있으면 고치기 전으로, 없으면 만든 블록 지우기)
        const stepBack = Boolean(cur && this.state.history.length);
        const pos = this.state.pos;
        const cardStyle = pos ? Object.assign({}, S.card, {left: `${pos.left}px`, top: `${pos.top}px`, right: 'auto', bottom: 'auto',
            maxHeight: `calc(100% - ${pos.top + 12}px)`}) : S.card;
        return (
            <div
                aria-label={L.title}
                ref={this.setCard}
                role="dialog"
                style={cardStyle}
            >
                <div
                    style={S.head}
                    title={L.dragTip}
                    onDoubleClick={this.handleDragReset}
                    onPointerDown={this.handleDragStart}
                >
                    <div style={S.title}>
                        {icon(GRIP_ICON, S.grip)}
                        {icon(ICONS.spark, {color: '#4C97FF'})}{L.title}
                    </div>
                    <button
                        aria-label={L.close}
                        style={S.close}
                        title={L.close}
                        onClick={this.handleClose}
                    >{icon(X_ICON)}</button>
                </div>
                <div style={S.body}>
                    {log.length ? (
                        // 대화 지우기는 끌기 손잡이(머리줄)가 아니라 지우는 대상(대화 칸) 바로 위에 둔다. 머리줄을 잡다가 잘못 눌리지 않게
                        <div style={S.logHead}>
                            <span style={Object.assign({}, S.label, {marginBottom: 0})}>{L.secChat}</span>
                            <button
                                style={S.logClear}
                                title={L.clearTip}
                                onClick={this.handleClearLog}
                            >{icon(CHAT_X_ICON)}{L.clear}</button>
                        </div>
                    ) : null}
                    {log.length ? (
                        <div
                            ref={this.setLogBox}
                            style={S.log}
                        >
                            {log.map((l, i) => (
                                <React.Fragment key={i}>
                                    {l.me ? <div style={S.me}>{l.me}</div> : null}
                                    <div style={Object.assign({}, S.bot, l.warn ? S.botWarn : {})}>
                                        {icon(l.warn ? WARN_ICON : CHECK_ICON, {color: l.warn ? '#d9822b' : '#0FBD8C', marginTop: '0.22rem', flex: 'none'})}
                                        <span>{l.bot}</span>
                                    </div>
                                </React.Fragment>
                            ))}
                        </div>
                    ) : <div style={S.sub}>{L.sub}</div>}
                    <textarea
                        ref={this.setInput}
                        placeholder={cur ? L.phRefine : L.phNew}
                        rows={2}
                        style={S.input}
                        value={this.state.text}
                        onChange={this.handleChange}
                        onKeyDown={this.handleKeyDown}
                    />
                    <div style={S.row}>
                        <button
                            disabled={this.state.aiBusy}
                            style={Object.assign({}, S.primary, this.state.aiBusy ? {opacity: 0.6, cursor: 'default'} : {})}
                            onClick={this.handleMake}
                        >{this.state.aiBusy ? L.ai.thinking : (cur ? L.send : L.make)}</button>
                        {micOn ? (
                            <button
                                aria-label={this.state.listening ? L.mic.stop : L.mic.start}
                                disabled={this.state.aiBusy}
                                style={Object.assign({}, S.secondary, this.state.listening ? {background: '#ffe9e6', borderColor: '#ff8a7a', color: '#c0392b'} : {})}
                                title={this.state.listening ? L.mic.stop : L.mic.start}
                                onClick={this.handleMic}
                            >{icon(MIC_ICON)}{this.state.listening ? L.mic.stop : L.mic.start}</button>
                        ) : null}
                        <button
                            disabled={!cur}
                            style={Object.assign({}, S.secondary, cur ? {} : {opacity: 0.45, cursor: 'default'})}
                            title={stepBack ? L.undoTipChange : L.undoTipBlocks}
                            onClick={this.handleUndo}
                        >{icon(stepBack ? UNDO_ICON : ICONS.trash)}{stepBack ? L.undoChange : L.undoBlocks}</button>
                    </div>
                    {this.state.listening ? <div style={Object.assign({}, S.note, {color: '#c0392b', marginTop: '0.45rem'})}>{icon(MIC_ICON)}{L.mic.listening}</div> : null}
                    {this.state.micAsk ? (
                        <div style={Object.assign({}, S.box, {marginTop: '0.55rem', fontSize: '0.78rem', color: '#4a5568'})}>
                            <div style={{marginBottom: '0.45rem', lineHeight: 1.45}}>{L.mic.ask}</div>
                            <div style={{display: 'flex', gap: '0.4rem'}}>
                                <button
                                    style={Object.assign({}, S.pick, {background: '#4C97FF', color: '#fff', border: 'none'})}
                                    onClick={this.handleMicOk}
                                >{L.mic.ok}</button>
                                <button
                                    style={S.pick}
                                    onClick={this.handleMicCancel}
                                >{L.mic.cancel}</button>
                            </div>
                        </div>
                    ) : null}
                    {showErr ? <div style={S.err}>{this.state.error}</div> : null}
                    {this.state.explain && !this.state.help ? (
                        <div style={S.section}>
                            <div style={S.label}>{this.state.explain.title}</div>
                            <div style={S.box}>
                                {this.state.explain.lines.map((l, i) => (
                                    <div
                                        key={`x${i}`}
                                        style={{padding: '0.08rem 0', marginLeft: `${l.depth * 0.95}rem`, fontWeight: l.head ? 700 : 400, color: '#2b3445'}}
                                    >{l.text}</div>
                                ))}
                                {this.state.explain.tail.map(t => (
                                    <div
                                        key={t}
                                        style={Object.assign({}, S.note, {marginTop: '0.3rem'})}
                                    >{icon(INFO_ICON)}{t}</div>
                                ))}
                            </div>
                        </div>
                    ) : null}
                    {this.state.help ? (
                        <div style={S.section}>
                            <div style={S.label}>{this.state.help.title}</div>
                            <div style={S.box}>
                                <div style={{color: '#4a5568', marginBottom: '0.4rem'}}>{this.state.help.text}</div>
                                <div style={S.chips}>
                                    {this.state.help.examples.map(t => (
                                        <button
                                            data-text={t}
                                            key={`h${t}`}
                                            style={Object.assign({}, S.chip, {background: '#fff'})}
                                            onClick={this.handleChip}
                                        >{t}</button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ) : null}
                    {cur ? this.renderAsks(this.state.view && this.state.view.asks) : null}
                    <div style={S.section}>
                        <div style={S.label}>{cur ? L.tryMore : L.tryNew}</div>
                        <div style={S.chips}>
                            {chips.map(t => (
                                <button
                                    data-text={t}
                                    key={t}
                                    style={S.chip}
                                    onClick={this.handleChip}
                                >{t}</button>
                            ))}
                        </div>
                    </div>
                    {this.renderView()}
                </div>
                {this.renderAi()}
            </div>
        );
    }
    // AI 도움 스위치 — 선생님이 키를 넣어 켠다(기본은 꺼짐 = 규칙 번역기만, 비용 0·이 브라우저 안에서만)
    renderAi () {
        const A = this.ui().ai;
        const on = this.state.aiOn;
        return (
            <div style={S.foot}>
                <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem'}}>
                    <span style={{display: 'flex', alignItems: 'center', gap: '0.3rem'}}>
                        {icon(ICONS.spark, {color: on ? '#0FBD8C' : '#9aa5b8'})}
                        <span style={{fontWeight: on ? 700 : 400, color: on ? '#0b8f6a' : '#6b7689'}}>{on ? A.on : A.off}</span>
                    </span>
                    <button
                        style={Object.assign({}, S.chip, {padding: '0.15rem 0.55rem'})}
                        onClick={this.handleAiToggle}
                    >{on ? A.turnOff : A.turnOn}</button>
                </div>
                {this.state.aiSetup && !on ? (
                    <div style={{marginTop: '0.45rem'}}>
                        <div style={{marginBottom: '0.4rem', lineHeight: 1.45}}>{A.privacy}</div>
                        <div style={{display: 'flex', gap: '0.4rem'}}>
                            <input
                                autoComplete="off"
                                placeholder={A.keyPh}
                                style={Object.assign({}, S.askInput, {flex: 1, width: 'auto'})}
                                type="password"
                                value={this.state.aiKey}
                                onChange={this.handleAiKeyInput}
                                onKeyDown={this.handleAiKeyDown}
                            />
                            <button
                                style={Object.assign({}, S.pick, {background: '#0FBD8C', color: '#fff', border: 'none'})}
                                onClick={this.handleAiKey}
                            >{A.turnOn}</button>
                        </div>
                    </div>
                ) : null}
            </div>
        );
    }
}

VibePanel.propTypes = {
    intl: intlShape.isRequired,
    vm: PropTypes.instanceOf(VM).isRequired
};

export default injectIntl(VibePanel);
